// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The geometry behind the annotation canvas, kept pure and I/O-free so it can be
// tested without a DOM. AnnotationCanvas.tsx does the drawing; this file decides
// where things go. (Same split as features/reconciliation/overBilling.ts.)

export type Point = { x: number; y: number };

export type Tool = 'rect' | 'redact' | 'arrow' | 'pen';

export type Shape =
  | { tool: 'rect'; from: Point; to: Point }
  | { tool: 'redact'; from: Point; to: Point }
  | { tool: 'arrow'; from: Point; to: Point }
  | { tool: 'pen'; points: Point[] };

/** High-contrast red — readable over both the light UI and any dark table row. */
export const INK = '#e11d48';
/** Opaque black: a redaction has to survive being screenshotted again. */
export const REDACT_FILL = '#000000';

export const LINE_WIDTH = 3;
export const ARROW_HEAD = 18;

/**
 * A pointer position in CSS pixels -> a position in the canvas's own pixel grid.
 *
 * These differ whenever the canvas is displayed at anything other than its
 * intrinsic size, which is always: the capture is taken at devicePixelRatio and
 * then fitted into the modal. Drawing with raw clientX/clientY puts every mark
 * in the wrong place on a HiDPI screen.
 */
export function toCanvasPoint(
  client: Point,
  bounds: { left: number; top: number; width: number; height: number },
  canvasWidth: number,
  canvasHeight: number
): Point {
  // A zero-width bounds means the canvas is not laid out yet; scaling by it
  // would produce Infinity/NaN coordinates that silently corrupt the shape list.
  const scaleX = bounds.width > 0 ? canvasWidth / bounds.width : 1;
  const scaleY = bounds.height > 0 ? canvasHeight / bounds.height : 1;
  return {
    x: (client.x - bounds.left) * scaleX,
    y: (client.y - bounds.top) * scaleY,
  };
}

/** Two opposite corners -> a rectangle with non-negative width/height. */
export function normalizeRect(from: Point, to: Point) {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

/**
 * The two wing points of an arrow head at `to`, coming from `from`.
 * Returns the tip first so a caller can stroke tip -> wing -> tip -> wing.
 */
export function arrowHeadPoints(from: Point, to: Point, size = ARROW_HEAD): [Point, Point] {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const spread = Math.PI / 7;
  return [
    { x: to.x - size * Math.cos(angle - spread), y: to.y - size * Math.sin(angle - spread) },
    { x: to.x - size * Math.cos(angle + spread), y: to.y - size * Math.sin(angle + spread) },
  ];
}

/**
 * A click that never became a drag. Dropping these matters for `redact` most of
 * all: a 1px black rectangle looks like nothing happened, so a reporter who
 * believes they blacked out a price would send the price.
 */
export function isDegenerate(shape: Shape, minPixels = 4): boolean {
  if (shape.tool === 'pen') return shape.points.length < 2;
  const { width, height } = normalizeRect(shape.from, shape.to);
  if (shape.tool === 'arrow') return Math.hypot(width, height) < minPixels;
  return width < minPixels || height < minPixels;
}

/** Drop the artifacts, keep the marks. */
export function pruneShapes(shapes: Shape[], minPixels = 4): Shape[] {
  return shapes.filter((s) => !isDegenerate(s, minPixels));
}
